/**
 * The panel view model (GET /api/v1/panel/snapshot and SSE payloads), C2 version of
 * apps/panel/src/api/types.ts with the reconciliations of specs/c2-kontratlar.md 2.10.
 * Money is integer micro-USD, every instant epoch milliseconds, agent ids are panel ids
 * (PANEL_AGENT_IDS). Turkish labels are produced by the API presenter; the panel does
 * not invent text.
 *
 * Changes against the pre-C2 panel types: ProposalBase gets payloadHash, actionType and
 * expiresAt; a GenericProposal card; requests carry sourceType and DesignRequest a
 * dueDate; the Claude quota percentages may be null; Handoff state adds failed;
 * DoneItem status adds dry_run, cancelled and queued; Snapshot gets system and rejected.
 */
import { z } from 'zod';
import { instantSchema, isoDateSchema, killSwitchLevelSchema, microUsdSchema, numericIdSchema, panelAgentIdSchema, rejectReasonCodeSchema, sha256Schema, tierSchema, } from './ids.js';
const id = z.string().min(1);
const text = z.string();
export const agentShapeSchema = z.enum(['circle', 'square', 'diamond', 'hexagon']);
export const agentStateSchema = z.enum(['working', 'waiting', 'idle', 'paused', 'error']);
export const tierTopicSchema = z.enum(['DIŞ', 'PARA', 'ÜRÜN']);
/** The Denetçi field has exactly three states and is never absent. */
export const verifierVerdictSchema = z.enum(['passed', 'rejected', 'unverified']);
export const verifierResultSchema = z.object({
    verdict: verifierVerdictSchema,
    at: instantSchema.nullable(),
    reason: text,
    model: z.string().nullable(),
});
export const checkSchema = z.object({
    key: text,
    value: text,
    valueMono: z.string().nullable(),
    result: z.enum(['passed', 'rejected', 'unverified', 'fact']),
});
export const sourceRefSchema = z.object({
    kind: z.enum(['kb', 'product']),
    key: text,
    title: text,
});
export const reasoningSchema = z.object({
    agent: panelAgentIdSchema,
    at: instantSchema,
    text,
    footnote: text,
    verdict: verifierVerdictSchema.nullable(),
});
/** What the executor does after approval; labels come from ACTION_REGISTRY, never from the UI. */
export const executorActionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('apply'),
        label: text,
        sublabel: text,
        desktopSublabel: text,
        consequence: text,
        toast: text,
    }),
    z.object({
        kind: z.literal('manual_copy'),
        label: text,
        sublabel: text,
        desktopSublabel: text,
        consequence: text,
        openLabel: text,
        openUrl: text,
        /**
         * v0.1.114: false when the text is not copied at approval (a delivery message: Twin makes the
         * download link after the approval and the linked text waits in Gönderilecekler). Absent: copied.
         */
        copyOnApprove: z.boolean().optional(),
    }),
    z.object({
        kind: z.literal('hold_apply'),
        label: text,
        sublabel: text,
        desktopSublabel: text,
        consequence: text,
        toast: text,
    }),
]);
export const keyValueSchema = z.object({
    key: text,
    value: text,
    note: z.string().nullable(),
});
/**
 * Where a card came from (v0.1.45): the original message, order, listing or person behind a
 * request or a proposal, with topic tags. Sercan: "kesinlikle orijinal kaynakları görelim".
 */
export const CARD_SOURCE_KINDS = [
    'gmail',
    'web_form',
    'etsy_message',
    'etsy_screenshot',
    'etsy_order',
    'etsy_listing',
    'whatsapp',
    'telegram',
    'lead',
    'sercan',
    'team',
    'system',
];
export const cardSourceSchema = z.object({
    kind: z.enum(CARD_SOURCE_KINDS),
    /** Short Turkish name of the source: "Gmail", "Etsy siparişi", "Sen". */
    label: text,
    /** The mail subject, the order line or the listing title. */
    title: z.string().nullable(),
    /** Who wrote or where it came from: the sender address, the handle, the receipt, the agent. */
    from: z.string().nullable(),
    at: instantSchema.nullable(),
    /** Opens the original (Gmail search by message id, the Etsy page); null when there is none. */
    link: z.string().nullable(),
    /** Topic tags: the vehicle, the party (GrabCAD), the country, the queue. */
    tags: z.array(text),
});
const proposalBaseShape = {
    /** approval_id as decimal digits; ticketId = proposalId = approvalId. */
    id,
    tier: tierSchema,
    topic: tierTopicSchema,
    kindLabel: text,
    title: text,
    summary: text,
    summaryLang: z.enum(['tr', 'en']),
    summaryPrefix: z.string().nullable(),
    agent: panelAgentIdSchema,
    runId: numericIdSchema,
    draftedAt: instantSchema,
    queuedAt: instantSchema,
    verifier: verifierResultSchema,
    waiting: z.string().nullable(),
    desktopNote: z.string().nullable(),
    checks: z.array(checkSchema),
    executor: executorActionSchema,
    reasoning: z.array(reasoningSchema),
    sources: z.array(sourceRefSchema),
    /** C2: the hash the panel sends back with approve, reject and hold. */
    payloadHash: sha256Schema,
    /** C2: twin.action_types.action_type. */
    actionType: z.string().min(1),
    /** C2: when the proposal expires. */
    expiresAt: instantSchema,
    /** v0.1.45: the original message, order or listing behind the proposal. */
    source: cardSourceSchema,
};
export const customerReplyProposalSchema = z.object({
    ...proposalBaseShape,
    kind: z.literal('customer_reply'),
    input: z.object({ key: text, quote: text, quoteTr: z.string().nullable(), note: text, desktopMeta: text }),
    /** Always drawn from the payload (draft.en = payload.reply_en). */
    draft: z.object({ en: text, tr: text }),
});
export const listingTitleProposalSchema = z.object({
    ...proposalBaseShape,
    kind: z.literal('listing_title'),
    diff: z.object({ before: text, after: text, rationale: text }),
});
export const couponProposalSchema = z.object({
    ...proposalBaseShape,
    kind: z.literal('coupon'),
    terms: z.object({
        code: text,
        discountPct: z.number().int(),
        days: z.number().int(),
        range: text,
        scope: text,
        listingCount: z.number().int().nonnegative(),
    }),
    policy: z.object({ capPct: z.number() }),
    impact: text,
});
/** C2: every other action type, so every T4 can be drawn and approved in the panel. */
export const genericProposalSchema = z.object({
    ...proposalBaseShape,
    kind: z.literal('generic'),
    fields: z.array(keyValueSchema),
    preview: z.object({ lang: z.enum(['tr', 'en']), text }).nullable(),
    diff: z.object({ before: text, after: text }).nullable(),
    /** v0.1.223: the text "Onayla ve kopyala" puts on the clipboard (a new Etsy listing draft); absent otherwise. */
    copyText: text.nullable().optional(),
});
export const proposalSchema = z.discriminatedUnion('kind', [
    customerReplyProposalSchema,
    listingTitleProposalSchema,
    couponProposalSchema,
    genericProposalSchema,
]);
/** Database request types (twin.requests.type). */
export const requestSourceTypeSchema = z.enum(['design', 'scan', 'shoot', 'decision', 'question']);
/** Panel request type: shoot and scan show as photo, decision as question. */
export const REQUEST_TYPE_BY_SOURCE = Object.freeze({ design: 'design', scan: 'photo', shoot: 'photo', decision: 'question', question: 'question' });
/** Panel request status: postponed shows as deferred, done as answered; cancelled is not shown. */
export const REQUEST_STATUS_BY_SOURCE = Object.freeze({
    open: 'open',
    accepted: 'accepted',
    postponed: 'deferred',
    rejected: 'rejected',
    done: 'answered',
});
const requestBaseShape = {
    id,
    title: text,
    agent: panelAgentIdSchema,
    createdAt: instantSchema,
    origin: text,
    context: text,
    customerWaiting: z.boolean(),
    status: z.enum(['open', 'accepted', 'deferred', 'rejected', 'answered']),
    statusNote: z.string().nullable(),
    /** C2: the real database type. */
    sourceType: requestSourceTypeSchema,
    /** v0.1.45: the original message, order or person behind the card. */
    source: cardSourceSchema,
};
/**
 * What the İstekler card promises under "Cevaplayınca" (v0.1.42). The API's answer route does
 * exactly this: a question raised from a customer conversation opens a Temsilci reply task
 * with the answer in its context; any question may add a knowledge base draft.
 */
/** v0.1.206: the two answers of a Denetçi finding card, upheld first; the API maps exactly these texts. */
export const FINDING_CHOICES_TR = Object.freeze(['Haklı: firmayı çıkar', 'Yanılıyor: firma kalsın']);
export const AFTER_ANSWER_TR = Object.freeze({
    /** v0.1.206: a Denetçi finding of the outreach audit, answered with one of its two choices. */
    auditFinding: 'Haklı dersen firma dalgadan çıkar ve taslakları iptal olur; Yanılıyor dersen firma dalgada kalır. Son itiraz da cevaplanınca denetim kapanır.',
    conversation: 'Cevabın kaydedilir. Temsilci bu cevapla müşteriye İngilizce bir cevap taslağı yazar; taslak Onaylar\'a düşer, müşteriye kendiliğinden hiçbir şey gitmez. "Bilgi bankasına ekle" işaretliyse İçerik ekibi kalıcı bir bilgi bankası kaydı hazırlar; o da onayına gelir.',
    standalone: 'Cevabın kaydedilir ve aynı soru bir daha sana gelmez. "Bilgi bankasına ekle" işaretliyse İçerik ekibi kalıcı bir bilgi bankası kaydı hazırlar; o da onayına gelir.',
});
export const questionRequestSchema = z.object({
    ...requestBaseShape,
    type: z.literal('question'),
    country: text,
    /** v0.1.42: the question in both languages, why the agent asks, the customer's words, the choices, what an answer triggers. */
    question: z.object({ en: text, tr: z.string().nullable() }),
    why: text,
    customerQuote: z.string().nullable(),
    options: z.array(text),
    afterAnswer: text,
    /** v0.1.206: false when only the listed choices are answers (a Denetçi finding); absent means free text too. */
    freeText: z.boolean().optional(),
});
export const designRequestSchema = z.object({
    ...requestBaseShape,
    type: z.literal('design'),
    inputs: z.array(text),
    acceptance: z.array(keyValueSchema),
    deliveryFolder: text,
    due: keyValueSchema,
    /** C2: the target date as a Europe/Istanbul day, next to the display text in due. */
    dueDate: isoDateSchema.nullable(),
    priceNote: text,
    desktopBrief: z.array(keyValueSchema),
});
export const photoRequestSchema = z.object({
    ...requestBaseShape,
    type: z.literal('photo'),
    facts: z.array(keyValueSchema),
    dueLabel: text,
    desktopSummary: text,
});
export const twinRequestSchema = z.discriminatedUnion('type', [
    questionRequestSchema,
    designRequestSchema,
    photoRequestSchema,
]);
export const agentBudgetSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('genesis'), spent: microUsdSchema, cap: microUsdSchema }),
    z.object({
        kind: z.literal('claude_max'),
        /** C2: null when there is no quota snapshot yet. */
        window5hPct: z.number().nullable(),
        weekPct: z.number().nullable(),
        apiEquivalent: microUsdSchema,
    }),
]);
export const agentSchema = z.object({
    id: panelAgentIdSchema,
    name: text,
    shortName: text,
    shape: agentShapeSchema,
    state: agentStateSchema,
    stateSince: instantSchema.nullable(),
    model: text,
    provider: text,
    task: text,
    desktopTask: text,
    shortTask: text,
    progress: z
        .object({
        runId: numericIdSchema,
        startedAt: instantSchema,
        doneSteps: z.number().int().nonnegative(),
        currentStep: text,
    })
        .nullable(),
    next: z.object({ at: instantSchema, label: text }).nullable(),
    lastRunAt: instantSchema.nullable(),
    waitingFor: z.string().nullable(),
    waitingNote: z.string().nullable(),
    waitingRunId: numericIdSchema.nullable(),
    todayLine: text,
    todayShort: text,
    linkedOutput: z.object({ label: text, ref: text }).nullable(),
    tileNote: z.string().nullable(),
    error: z.object({ message: text, attempts: z.number().int().nonnegative(), at: instantSchema }).nullable(),
    budget: agentBudgetSchema,
});
export const artifactSchema = z.object({
    kind: z.enum(['image', 'kb', 'product', 'draft', 'queue']),
    label: text,
});
export const runStepSchema = z.object({
    at: instantSchema,
    text,
    lead: z.string().nullable(),
    /** run_steps kind verify -> verifier, queue -> queue, everything else step. */
    node: z.enum(['step', 'verifier', 'queue']),
    artifact: artifactSchema.nullable(),
    verdict: verifierVerdictSchema.nullable(),
});
export const runSchema = z.object({
    id: numericIdSchema,
    agent: panelAgentIdSchema,
    title: text,
    subtitle: text,
    status: z.enum(['running', 'done', 'failed']),
    startedAt: instantSchema,
    finishedAt: instantSchema.nullable(),
    steps: z.array(runStepSchema),
    cost: microUsdSchema,
    provenance: z.array(text),
    trigger: text,
    authorModel: text,
    verifierModel: z.string().nullable(),
    authorSeconds: z.number().nonnegative().nullable(),
    verifierSeconds: z.number().nonnegative().nullable(),
});
/** Database handoff state in_progress -> running, failed and rejected -> failed; cancelled is not shown. */
export const handoffStateSchema = z.enum(['waiting', 'passed', 'running', 'failed']);
export const handoffSchema = z.object({
    id,
    at: instantSchema,
    from: panelAgentIdSchema,
    to: panelAgentIdSchema,
    subject: text,
    note: z.string().nullable(),
    state: handoffStateSchema,
    resolvedAt: instantSchema.nullable(),
    runId: numericIdSchema.nullable(),
    queuedTier: tierSchema.nullable(),
});
export const liveFeedItemSchema = z.object({
    id,
    at: instantSchema,
    agent: panelAgentIdSchema,
    text,
    runId: numericIdSchema,
});
export const storeSummarySchema = z.object({
    monthLabel: text,
    monthShort: text,
    revenue: microUsdSchema,
    orders: z.number().int().nonnegative(),
    newCountries: z.number().int().nonnegative(),
    countries: z.array(z.object({ code: text, orders: z.number().int().nonnegative() })),
    sixMonthAverage: microUsdSchema,
    allTime: z.object({
        revenue: microUsdSchema,
        orders: z.number().int().nonnegative(),
        countries: z.number().int().nonnegative(),
        reviews: z.number().int().nonnegative(),
        rating: z.number().nonnegative(),
    }),
    monthly: z.array(z.object({ label: text, year: z.number().int(), amount: microUsdSchema })),
    sales: z.array(z.object({ id, at: instantSchema, country: text, title: text, amount: microUsdSchema })),
    activeListings: z.number().int().nonnegative(),
});
export const scheduleItemSchema = z.object({
    at: instantSchema,
    label: text,
    shortLabel: text,
    owner: z.union([panelAgentIdSchema, z.literal('executor')]),
    done: z.boolean(),
});
/**
 * Yapılanlar row. execution_status mapping (2.12): executed/verified -> applied,
 * manual_pending -> awaiting_send, manual_done -> sent, failed -> failed, dry_run ->
 * dry_run, cancelled -> cancelled (undo included), scheduled after the undo window and
 * executing -> queued; scheduled inside the window is not a done row.
 */
export const doneStatusSchema = z.enum([
    'applied',
    'awaiting_send',
    'failed',
    'sent',
    'dry_run',
    'cancelled',
    'queued',
]);
export const doneItemSchema = z.object({
    /** approval_id. */
    id,
    at: instantSchema,
    title: text,
    result: text,
    actor: z.enum(['executor', 'sercan']),
    /** Only rows of listing_text_update with a verified execution and a before_state. */
    revertible: z.boolean(),
    status: doneStatusSchema,
});
/** One message of the conversation behind an outbox item (v0.1.54). */
export const outboxMessageSchema = z.object({
    /** message_id. */
    id,
    direction: z.enum(['in', 'out']),
    at: instantSchema.nullable(),
    text,
    textTr: text.nullable(),
});
/**
 * Gönderilecekler (v0.1.54): an approved e-mail reply that waits for Sercan's Gönder (or was
 * sent in the last day), with everything he reads before sending.
 */
export const outboxItemSchema = z.object({
    /** approval_id. */
    id,
    conversationId: id,
    title: text,
    subject: text,
    /** Whom the worker will address; null when no address could be found (send by hand). */
    to: text.nullable(),
    approvedAt: instantSchema,
    customer: z.object({ en: text, tr: text.nullable() }),
    reply: z.object({ en: text, tr: text.nullable() }),
    thread: z.array(outboxMessageSchema),
    send: z.object({
        status: z.enum(['idle', 'queued', 'sending', 'sent', 'failed']),
        error: text.nullable(),
        at: instantSchema.nullable(),
    }),
});
/**
 * Teslimat (v0.1.114): an approved delivery message waiting for Sercan's hand send. The worker
 * makes the download link on the BMU Portal after the approval; `reply.en` carries the link in
 * place of [LINK] once it is ready (until then, or after a failure, the placeholder stays).
 */
export const deliveryItemSchema = z.object({
    /** approval_id. */
    id,
    orderId: text,
    title: text,
    approvedAt: instantSchema,
    reply: z.object({ en: text, tr: text.nullable() }),
    link: z.object({
        status: z.enum(['waiting', 'ready', 'failed', 'no_files', 'no_connection']),
        url: text.nullable(),
        /** Why there is no link, in Turkish; null when ready or still being made. */
        note: text.nullable(),
    }),
    /** Gönderdim was pressed (shown for a day after). */
    sent: z.boolean(),
});
/**
 * Gönderilecekler › Yeni Etsy ilanları (v0.1.223): an approved listing_new_draft. Twin cannot create Etsy listings;
 * Sercan copies each field into a new listing and presses Yaptım.
 */
export const listingDraftItemSchema = z.object({
    /** approval_id. */
    id,
    title: text,
    sku: text,
    approvedAt: instantSchema,
    titleEn: text,
    tags: z.array(text),
    bodyEn: text,
    /** The catalog price as text ("$85.00"); null when the product has none. */
    price: text.nullable(),
    /** Yaptım was pressed (shown for a day after). */
    done: z.boolean(),
});
/** C2: kill switch and dry run (K8: policies.yaml OR system_state), and the API boot id. */
export const panelSystemSchema = z.object({
    killSwitch: z.object({
        level: killSwitchLevelSchema,
        since: instantSchema.nullable(),
        reason: z.string().nullable(),
    }),
    dryRun: z.boolean(),
    bootId: z.string().regex(/^[0-9a-z]{1,16}$/),
});
/** Hazırlık (T05): the state of one capability, technical and business sides kept apart. */
export const readinessLevelSchema = z.enum(['ready', 'partial', 'waiting', 'off']);
export const readinessFactSchema = z.object({ label: text, ok: z.boolean() });
export const readinessItemSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
    title: text,
    level: readinessLevelSchema,
    /** Is the service behind it alive (worker signal, reader cadence, kill switch). */
    service: readinessFactSchema,
    /** Are the values entered, tested, the grant given. */
    connection: readinessFactSchema,
    /** Is there data to work with. */
    data: readinessFactSchema,
    /** The policy or human gate in front of the next step, when there is one. */
    policy: readinessFactSchema.nullable(),
    lastSuccessAt: instantSchema.nullable(),
    nextAt: instantSchema.nullable(),
    /** What Sercan can do now, as a panel path; null when only Twin can move it. */
    action: z.object({ label: text, to: text }).nullable(),
    note: z.string().nullable(),
});
/** Bugün must not say "nothing waits" while these wait (v0.1.39). */
export const attentionSchema = z.object({
    /** Customer mails without a reply proposal yet: their tasks are open, assigned, in rework or failed. */
    unansweredMails: z.number().int().nonnegative(),
    /** Of those, tasks whose last attempt failed: the agent is stuck, not slow. */
    failedMails: z.number().int().nonnegative(),
    openSekreterItems: z.number().int().nonnegative(),
    /**
     * Agents whose last run failed and that have not succeeded since, with the reason in words and
     * how many runs in a row failed in the last day (v0.1.51: the headline names the streak).
     */
    stoppedAgents: z.array(z.object({ id: panelAgentIdSchema, name: text, reason: text, streak: z.number().int().nonnegative() })),
});
/** Bugün yapılanlar (v0.1.65): the day's counts since the local midnight. */
export const todayCountsSchema = z.object({
    mailsIn: z.number().int().nonnegative(),
    messagesIn: z.number().int().nonnegative(),
    drafts: z.number().int().nonnegative(),
    verifyPassed: z.number().int().nonnegative(),
    verifyRejected: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative(),
    edited: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    sentGmail: z.number().int().nonnegative(),
    ownReplies: z.number().int().nonnegative(),
    questionsAsked: z.number().int().nonnegative(),
    questionsAnswered: z.number().int().nonnegative(),
    orders: z.number().int().nonnegative(),
    revenueCents: z.number().int().nonnegative(),
    companiesFound: z.number().int().nonnegative(),
    leadsQualified: z.number().int().nonnegative(),
    leadsContacted: z.number().int().nonnegative(),
    leadsReplied: z.number().int().nonnegative(),
    runs: z.number().int().nonnegative(),
    runsFailed: z.number().int().nonnegative(),
    costMicroUsd: z.number().int().nonnegative(),
    tasksDone: z.number().int().nonnegative(),
});
/** One Turkish line of the block, with the page that holds the detail. */
export const todayLineSchema = z.object({ key: text, text, link: text });
export const todaySchema = z.object({
    since: instantSchema,
    asOf: instantSchema,
    counts: todayCountsSchema,
    lines: z.array(todayLineSchema),
});
/**
 * Bugün's four business tiles (panel audit 25 Oct 2026): each number with what it is compared against
 * and fourteen daily points for its sparkline, so the first screen leads with money and results.
 */
const pulseCount = z.number().int().nonnegative();
export const pulseSchema = z.object({
    /** The fourteen local days (twin.report_tz) of the revenue, order and outreach series, oldest first, ending today. */
    days: z.array(text),
    revenue: z.object({
        /** This month so far: orders plus paid deals, the same sum as store.revenue. */
        monthToDate: microUsdSchema,
        /** Last month from its first day to the same day of the month and the same hour. */
        lastMonthToDate: microUsdSchema,
        daily: z.array(microUsdSchema),
    }),
    orders: z.object({ monthToDate: pulseCount, lastMonthToDate: pulseCount, daily: z.array(pulseCount) }),
    /**
     * Entries from outside to the site's pages (server log, people only), the seven days up to the last day the log
     * has (through) against the seven before; daily is the fourteen days ending at through. Null through: no log yet.
     */
    visits: z.object({
        last7: pulseCount,
        previous7: pulseCount,
        through: text.nullable(),
        daily: z.array(pulseCount),
    }),
    /** Outreach in the last seven days against the seven before: mails sent, firms that answered, interested ones. */
    outreach: z.object({
        sent7: pulseCount,
        replied7: pulseCount,
        interested7: pulseCount,
        previousReplied7: pulseCount,
        previousInterested7: pulseCount,
        /** Firms that answered, per day of `days`. */
        daily: z.array(pulseCount),
    }),
    /** Approval cards whose time ran out yesterday (local day) without a decision. */
    expiredYesterday: pulseCount,
});
export const snapshotSchema = z.object({
    now: instantSchema,
    system: panelSystemSchema,
    proposals: z.array(proposalSchema),
    requests: z.array(twinRequestSchema),
    agents: z.array(agentSchema),
    handoffs: z.array(handoffSchema),
    runs: z.array(runSchema),
    feed: z.array(liveFeedItemSchema),
    store: storeSummarySchema,
    genesis: z.object({ spent: microUsdSchema, cap: microUsdSchema }),
    executor: z.object({
        ready: z.boolean(),
        pending: z.number().int().nonnegative(),
        lastAppliedAt: instantSchema.nullable(),
        undoSeconds: z.number().int().nonnegative(),
    }),
    policy: z.object({ discountCapPct: z.number(), undoSeconds: z.number().int().nonnegative() }),
    schedule: z.array(scheduleItemSchema),
    scheduleNote: text,
    gmNote: z.object({ at: instantSchema, model: text, lead: text, body: text, source: text, link: text }),
    done: z.array(doneItemSchema),
    requestSlots: z.object({ used: z.number().int().nonnegative(), max: z.number().int().nonnegative() }),
    /** C2: rejected approvals of the last 7 days. */
    rejected: z.array(z.object({ proposalId: id, title: text, reasonCode: rejectReasonCodeSchema, at: instantSchema })),
    /** What the queue does not show but Sercan must see (v0.1.39). */
    attention: attentionSchema,
    /** Bugün yapılanlar (v0.1.65). */
    today: todaySchema,
    /** Gönderilecekler (v0.1.54). */
    outbox: z.array(outboxItemSchema),
    /** Gönderilecekler › Teslimat (v0.1.114): approved delivery messages with their download link. */
    deliveries: z.array(deliveryItemSchema).default([]),
    /** Gönderilecekler › Yeni Etsy ilanları (v0.1.223): approved new listing drafts to copy into Etsy. */
    listingDrafts: z.array(listingDraftItemSchema).default([]),
    /** Hazırlık (T05, v0.1.12): the capability rows of Bugün. */
    readiness: z.array(readinessItemSchema),
    /** Bugün's business tiles (audit 25 Oct 2026); null from an API that does not build them yet. */
    pulse: pulseSchema.nullable().default(null),
});
/**
 * ApprovalTicket from a decision response: ticketId = proposalId = approvalId,
 * approvedAt = decidedAt, applyAt = null for manual_pending, else undoUntil.
 */
export function approvalTicketFrom(response) {
    const approvedAt = response.decidedAt === null ? Number.NaN : Date.parse(response.decidedAt);
    const applyAt = response.executionStatus === 'manual_pending' || response.undoUntil === null
        ? null
        : Date.parse(response.undoUntil);
    return { ticketId: response.approvalId, proposalId: response.approvalId, approvedAt, applyAt };
}
//# sourceMappingURL=panel.js.map